import React, { useEffect, useRef } from 'react';
import { X, LogOut } from 'lucide-react';

/**
 * MobileDrawer
 * Accessible, production-ready slide-in navigation drawer for mobile and tablets (< lg).
 * Features: ESC key close, backdrop overlay click, focus trap, body scroll lock, safe-area support, active page highlighting.
 */
export const MobileDrawer = ({
  isOpen,
  onClose,
  navItems = [],
  currentPage,
  onNavigate,
  onLogout,
  user,
  profileGuardActive = false,
  profileGuardAllowedPage = null,
}) => {
  const drawerRef = useRef(null);
  const triggerRef = useRef(null);

  // Preserve scroll position & lock body scroll when open
  useEffect(() => {
    if (isOpen) {
      triggerRef.current = document.activeElement;
      document.body.style.overflow = 'hidden';

      // Focus first focusable element inside drawer
      const firstBtn = drawerRef.current?.querySelector('button, [tabindex="0"]');
      if (firstBtn && typeof firstBtn.focus === 'function') {
        setTimeout(() => firstBtn.focus(), 50);
      }
    } else {
      document.body.style.overflow = '';
      if (triggerRef.current && typeof triggerRef.current.focus === 'function') {
        triggerRef.current.focus();
      }
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // ESC key listener
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex lg:hidden bg-black/60 backdrop-blur-xs overlay-enter"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Navigation menu"
    >
      <aside
        ref={drawerRef}
        className="w-[280px] sm:w-[320px] max-w-[85vw] h-full overflow-y-auto text-white flex flex-col shadow-2xl drawer-enter safe-top safe-bottom"
        style={{
          backgroundColor: '#000000',
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)',
          backgroundSize: '28px 28px',
        }}
      >
        {/* Header with logo & close button */}
        <div className="px-5 py-5 border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center text-sm font-bold shadow-md">FM</div>
            <div>
              <p className="text-lg font-bold leading-none">FindMyLawyer</p>
              <p className="text-xs text-slate-300 mt-1">Lawyer Portal</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-xl bg-white/10 text-slate-200 hover:text-white hover:bg-white/20 flex items-center justify-center transition-colors shrink-0"
            style={{ minWidth: '44px', minHeight: '44px' }}
            aria-label="Close menu"
          >
            <X size={20} />
          </button>
        </div>

        {/* User Profile Summary */}
        <div
          className="px-5 py-4 border-b border-white/10 cursor-pointer hover:bg-white/5 transition-all group"
          onClick={() => {
            onNavigate('settings', { activeTab: 'profile' });
            onClose();
          }}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-500 flex items-center justify-center text-sm font-semibold group-hover:shadow-[0_0_12px_rgba(59,130,246,0.6)] transition-shadow overflow-hidden shrink-0">
              {user?.profilePhoto || user?.profile_photo_url ? (
                <img src={user.profilePhoto || user.profile_photo_url} alt={user?.name || "Profile"} className="w-full h-full object-cover" />
              ) : (
                user?.name?.[0] || 'A'
              )}
            </div>
            <div className="min-w-0">
              <p className="font-semibold truncate group-hover:text-blue-400 transition-colors text-sm sm:text-base">{user?.name || 'Advocate'}</p>
              <p className="text-xs text-slate-400 mt-0.5 truncate">View profile & settings →</p>
            </div>
          </div>
        </div>

        {/* Navigation Items */}
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = currentPage === item.id;

            return (
              <button
                key={item.id}
                onClick={() => {
                  onNavigate(item.id);
                  onClose();
                }}
                disabled={
                  profileGuardActive &&
                  profileGuardAllowedPage != null &&
                  item.id !== profileGuardAllowedPage
                }
                style={{ minHeight: '44px' }}
                className={`w-full text-left flex items-center justify-between px-4 py-3 rounded-xl transition-colors ${
                  isActive ? 'bg-blue-600 font-bold text-white shadow-md' : 'text-slate-300 hover:bg-slate-800'
                } ${
                  profileGuardActive &&
                  profileGuardAllowedPage != null &&
                  item.id !== profileGuardAllowedPage
                    ? 'opacity-50 cursor-not-allowed hover:bg-transparent'
                    : ''
                }`}
              >
                <span className="flex items-center gap-3 text-sm font-medium">
                  {Icon && <Icon size={18} className={isActive ? 'text-white' : 'text-slate-400'} />}
                  {item.label}
                </span>
                {item.badge && (
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                      item.badge === 'AI' ? 'bg-indigo-500 text-white' : 'bg-blue-500 text-white'
                    }`}
                  >
                    {item.badge}
                  </span>
                )}
              </button>
            );
          })}
        </nav>

        {/* Logout Button on Mobile */}
        <div className="p-4 border-t border-white/10 mt-auto">
          {onLogout && (
            <button
              onClick={() => {
                onClose();
                onLogout();
              }}
              style={{ minHeight: '44px' }}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-slate-800 hover:bg-red-600 hover:text-white text-slate-200 font-semibold transition-colors shadow-sm"
            >
              <LogOut size={16} />
              Logout
            </button>
          )}
          <p className="text-center text-[11px] text-slate-500 mt-3">Subscription: Active</p>
        </div>
      </aside>
    </div>
  );
};
